import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AccountBalanceWallet,
  CheckCircle,
  Warning,
  Close
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getFirestore } from 'firebase/firestore';
import { API_URL } from '../config/api';

interface StripeActivationDialogProps {
  open: boolean;
  onClose: () => void;
  onActivate: () => void;
  required?: boolean; // Si true, pas de bouton "Plus tard"
}

const StripeActivationDialog: React.FC<StripeActivationDialogProps> = ({
  open,
  onClose,
  onActivate,
  required = false
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🔵 [FRONTEND] Appel API:', `${API_URL}/api/stripe/create-connected-account`);
      console.log('🔵 [FRONTEND] UserId:', user.uid);
      console.log('🔵 [FRONTEND] Email:', user.email);

      const response = await fetch(`${API_URL}/api/stripe/create-connected-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          userEmail: user.email
        })
      }).catch((fetchError) => {
        console.error('❌ [FRONTEND] Erreur de connexion:', fetchError);
        throw new Error(`Impossible de se connecter au serveur (${API_URL}). Vérifiez que le backend est accessible et que CORS est configuré.`);
      });

      console.log('🔵 [FRONTEND] Response status:', response.status);
      
      // Vérifier si la réponse est OK
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur serveur' }));
        console.error('❌ [FRONTEND] Erreur HTTP:', response.status, errorData);
        throw new Error(errorData.error || errorData.message || `Erreur serveur (${response.status})`);
      }

      const data = await response.json();
      console.log('🔵 [FRONTEND] Response data:', data);

      if (data.success) {
        // Gérer les deux formats de réponse (snake_case et camelCase)
        const accountId = data.accountId || data.account_id;
        const onboardingUrl = data.onboardingUrl || data.onboarding_url;

        console.log('✅ [FRONTEND] Compte Connect créé avec succès');
        console.log('✅ [FRONTEND] AccountId:', accountId);
        console.log('✅ [FRONTEND] Onboarding URL:', onboardingUrl);

        if (!accountId || !onboardingUrl) {
          console.error('❌ [FRONTEND] Données manquantes:', { accountId, onboardingUrl });
          setError('Données de réponse incomplètes. Veuillez réessayer.');
          setLoading(false);
          return;
        }

        // Sauvegarder l'accountId en base
        try {
          const db = getFirestore();
          await updateDoc(doc(db, 'users', user.uid), {
            'stripe.connectedAccountId': accountId,
            'stripe.onboardingCompleted': false
          });
          console.log('✅ [FRONTEND] AccountId sauvegardé en base');
        } catch (dbError) {
          console.error('⚠️ [FRONTEND] Erreur sauvegarde en base (non bloquant):', dbError);
          // Ne pas bloquer la redirection si la sauvegarde échoue
        }

        // Rediriger vers l'onboarding Stripe
        console.log('🔵 [FRONTEND] Redirection vers:', onboardingUrl);
        window.location.href = onboardingUrl;
      } else {
        console.error('❌ [FRONTEND] Échec création compte Connect:', data);
        const errorMessage = data.error || data.message || 'Erreur lors de l\'activation des paiements';
        setError(errorMessage);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('❌ [FRONTEND] Erreur activation Stripe:', err);
      console.error('❌ [FRONTEND] Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // Message d'erreur plus détaillé
      const errorMessage = err.message || 'Erreur lors de l\'activation des paiements. Vérifiez votre connexion et réessayez.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (!required) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={required ? undefined : handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWallet sx={{ color: '#C9B6AB', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#C9B6AB' }}>
            Activation des paiements
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Activez vos paiements pour recevoir automatiquement l'argent de vos locations de robes
        </Typography>

        {required ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Paiements requis
            </Typography>
            <Typography variant="body2">
              Vous devez activer vos paiements Stripe pour pouvoir {required ? 'publier une robe en location' : 'continuer'}.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Complétez l'onboarding Stripe pour activer les paiements. Cela prend moins de 5 minutes.
            </Typography>
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Ce dont vous aurez besoin :
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Vos informations personnelles (nom, adresse, date de naissance)" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Votre IBAN pour les virements" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Une pièce d'identité (photo)" />
          </ListItem>
        </List>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        {!required && (
          <Button
            onClick={handleSkip}
            disabled={loading}
            sx={{ color: '#999' }}
          >
            Plus tard
          </Button>
        )}
        <Button
          onClick={handleActivate}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceWallet />}
          sx={{
            backgroundColor: '#C9B6AB',
            '&:hover': {
              backgroundColor: '#C1A08E',
            },
          }}
        >
          {loading ? 'Activation...' : 'Activer mes paiements'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StripeActivationDialog;

